import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import HistoryIcon from "@mui/icons-material/History";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  deleteMyProject,
  duplicateMyProject,
  exportMyProject,
  getMyProjectRevisions,
  getMyProjects,
  getMySummary,
  renameMyProject,
  restoreMyProjectRevision,
} from "../api/apiClient";

/**
 * User Dashboard — deliberately separate from the Company Admin console.
 *
 * Shows only the signed-in user's own saved projects. The API is scoped by
 * the JWT, so this page cannot request anyone else's work even if asked to.
 *
 * Projects are kept for 90 days from the user's last activity; this page warns
 * before anything is deleted.
 */

const STATUS_COLORS = {
  PASS: "success",
  FAIL: "error",
  WARNING: "warning",
  BLOCKED: "error",
  "NOT RUN": "default",
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const formatDay = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

function StatCard({ label, value, hint, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, lineHeight: 1.1, color }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

/** How close a project is to being deleted, as a chip the user can read at a glance. */
function RetentionCell({ project }) {
  const days = project.days_until_deletion;
  if (days === null || days === undefined) return <Typography variant="body2">—</Typography>;

  if (project.expiring_soon) {
    return (
      <Tooltip title={`Will be deleted on ${formatDay(project.expires_at)} unless you open it`}>
        <Chip
          icon={<WarningAmberIcon />}
          label={days === 0 ? "Due for deletion" : `${days} day${days === 1 ? "" : "s"} left`}
          size="small"
          color="warning"
          sx={{ fontWeight: 700 }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={`Kept until ${formatDay(project.expires_at)}`}>
      <Typography variant="body2" color="text.secondary">
        {days} days
      </Typography>
    </Tooltip>
  );
}

export default function UserDashboardPage() {
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuProject, setMenuProject] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [historyProject, setHistoryProject] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, projectData] = await Promise.all([getMySummary(), getMyProjects()]);
      setSummary(summaryData);
      setProjects(projectData.projects || []);
    } catch (err) {
      console.error("Failed to load user dashboard", err);
      setError("Could not load your dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => summary?.projects || {}, [summary]);
  const retentionDays = summary?.retention_days ?? 90;
  const expiringSoon = useMemo(() => projects.filter((p) => p.expiring_soon), [projects]);

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuProject(null);
  };

  /** Open/continue: the workflow page loads the saved state for this project. */
  const handleOpen = (project) => {
    navigate(`/build?project=${encodeURIComponent(project.session_id)}`);
  };

  /** Second Pair of Eyes: check a completed build against this project's approved blueprint. */
  const handleCheckBuild = (project) => {
    closeMenu();
    navigate(`/build/projects/${encodeURIComponent(project.session_id)}/second-pair-of-eyes`);
  };

  const handleRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    setBusy(true);
    try {
      await renameMyProject(renameTarget.session_id, renameValue.trim());
      setRenameTarget(null);
      setNotice("Project renamed.");
      await load();
    } catch (err) {
      console.error("Rename failed", err);
      setError("Could not rename that project.");
    } finally {
      setBusy(false);
    }
  };

  const handleDuplicate = async (project) => {
    closeMenu();
    setBusy(true);
    try {
      const { project: copy } = await duplicateMyProject(project.session_id);
      setNotice(`Duplicated as “${copy.title}”.`);
      await load();
    } catch (err) {
      console.error("Duplicate failed", err);
      setError("Could not duplicate that project.");
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async (project) => {
    closeMenu();
    setBusy(true);
    try {
      const filename = await exportMyProject(project.session_id, project.title);
      setNotice(`Downloaded ${filename}.`);
    } catch (err) {
      console.error("Export failed", err);
      setError("Could not export that project.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteMyProject(pendingDelete.session_id);
      setPendingDelete(null);
      setNotice("Project deleted.");
      await load();
    } catch (err) {
      console.error("Delete failed", err);
      setError("Could not delete that project.");
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async (project) => {
    closeMenu();
    setHistoryProject(project);
    setRevisionsLoading(true);
    try {
      const data = await getMyProjectRevisions(project.session_id);
      setRevisions(data.revisions || []);
    } catch (err) {
      console.error("Could not load revisions", err);
      setRevisions([]);
      setError("Could not load the revision history.");
    } finally {
      setRevisionsLoading(false);
    }
  };

  const handleRestore = async (revisionNumber) => {
    if (!historyProject) return;
    setBusy(true);
    try {
      await restoreMyProjectRevision(historyProject.session_id, revisionNumber);
      setHistoryProject(null);
      setNotice(`Restored revision ${revisionNumber}.`);
      await load();
    } catch (err) {
      console.error("Restore failed", err);
      setError("Could not restore that revision.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 0.5, sm: 1 }, py: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="h4" fontWeight={800}>
            My Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {summary?.name ? `Welcome back, ${summary.name}. ` : ""}
            Pick up any project where you left off.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={load} variant="outlined">
            Refresh
          </Button>
          <Button component={Link} to="/build" variant="contained">
            New Blueprint
          </Button>
        </Stack>
      </Stack>

      <Chip
        icon={<LockOutlinedIcon />}
        label="Private to you — not visible to company admins"
        size="small"
        color="success"
        variant="outlined"
        sx={{ mb: 2.5, fontWeight: 600 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {expiringSoon.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          <strong>
            {expiringSoon.length} project{expiringSoon.length === 1 ? "" : "s"} will be deleted soon.
          </strong>{" "}
          Projects are kept for {retentionDays} days after you last work on them. Open one to keep
          it, or download it before it goes.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Projects" value={counts.total ?? 0} hint="Saved by you" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Completed" value={counts.pass ?? 0} hint="Ready for coding" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Expiring Soon"
            value={counts.expiring_soon ?? 0}
            hint={`Within ${summary?.warning_days ?? 7} days`}
            color={(counts.expiring_soon ?? 0) > 0 ? "warning.main" : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Build Credits" value={summary?.build_credits ?? 0} hint="Available to spend" />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            My Projects
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Everything you need to resume a blueprint is saved — your inputs, the generated
            output, your settings and the revision history.
          </Typography>
        </Box>
        <Divider />
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 820 }} aria-label="my projects">
            <TableHead>
              <TableRow>
                <TableCell><strong>Project</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Revisions</strong></TableCell>
                <TableCell><strong>Last Activity</strong></TableCell>
                <TableCell><strong>Kept For</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      You have not saved any projects yet.
                    </Typography>
                    <Button component={Link} to="/build" variant="contained" size="small">
                      Create your first blueprint
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                projects.map((project) => (
                  <TableRow
                    key={project.session_id}
                    hover
                    sx={project.expiring_soon ? { bgcolor: "rgba(255,167,38,0.08)" } : undefined}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {project.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                        {project.session_id.slice(0, 8)}…
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={project.status || "NOT RUN"}
                        size="small"
                        color={STATUS_COLORS[project.status] || "default"}
                      />
                    </TableCell>
                    <TableCell>{project.revision_count ?? 0}</TableCell>
                    <TableCell>{formatDate(project.last_activity_at)}</TableCell>
                    <TableCell><RetentionCell project={project} /></TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleOpen(project)}
                        sx={{ mr: 0.5 }}
                      >
                        Open
                      </Button>
                      <Tooltip title="Second Pair of Eyes — Check My Build">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleCheckBuild(project)}
                          aria-label={`Check build for ${project.title}`}
                          sx={{ mr: 0.5 }}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="More actions">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setMenuProject(project);
                          }}
                          aria-label={`Actions for ${project.title}`}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            setRenameTarget(menuProject);
            setRenameValue(menuProject?.title || "");
            closeMenu();
          }}
        >
          <DriveFileRenameOutlineIcon fontSize="small" sx={{ mr: 1.5 }} /> Rename
        </MenuItem>
        <MenuItem onClick={() => handleDuplicate(menuProject)}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1.5 }} /> Duplicate
        </MenuItem>
        <MenuItem onClick={() => handleExport(menuProject)}>
          <DownloadOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} /> Download / Export
        </MenuItem>
        <MenuItem onClick={() => openHistory(menuProject)}>
          <HistoryIcon fontSize="small" sx={{ mr: 1.5 }} /> Revision history
        </MenuItem>
        <MenuItem onClick={() => handleCheckBuild(menuProject)}>
          <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} /> Second Pair of Eyes — Check My Build
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setPendingDelete(menuProject);
            closeMenu();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Rename project</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Project name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleRename()}
            inputProps={{ maxLength: 255 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)}>Cancel</Button>
          <Button onClick={handleRename} variant="contained" disabled={busy || !renameValue.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete “{pendingDelete?.title}”?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the project and its revision history from your account. This
            cannot be undone — download it first if you want to keep a copy.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={busy}>
            {busy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(historyProject)}
        onClose={() => setHistoryProject(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Revision history — {historyProject?.title}</DialogTitle>
        <DialogContent dividers>
          {revisionsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : revisions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No earlier revisions yet. One is saved each time this project's work changes.
            </Typography>
          ) : (
            <List dense>
              {revisions.map((revision) => (
                <ListItem
                  key={revision.id}
                  secondaryAction={
                    <Button size="small" onClick={() => handleRestore(revision.revision_number)} disabled={busy}>
                      Restore
                    </Button>
                  }
                >
                  <ListItemText
                    primary={`Revision ${revision.revision_number}${revision.label ? ` — ${revision.label}` : ""}`}
                    secondary={formatDate(revision.created_at)}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryProject(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
